import React, { FC, ReactElement } from 'react';

import { useIntl } from 'react-intl';

import { IPaginationProps } from './pagination.interface';
import styles from './pagination.module.scss';
import { getPaginationItems } from './pagination.utils';

import { Button } from 'views/components/UI/Button';
import { ArrowLeft, ArrowRight } from 'views/components/UI/Icons';

const DEFAULT_ITEMS_PER_PAGE = 20;

const Pagination: FC<IPaginationProps> = props => {
  const {
    totalItems = 0,
    itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
    currentPage = 1,
    handlePageChange,
  } = props;
  const intl = useIntl();
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalPages <= 1) {
    return null;
  }

  const page = Math.min(Math.max(currentPage, 1), totalPages);

  return (
    <nav
      className={styles.pagination}
      aria-label={intl.formatMessage({ id: 'pagination.label' })}
    >
      <Button
        size="small"
        mode="tertiary"
        color="dark"
        icon={<ArrowLeft color="currentColor" />}
        aria-label={intl.formatMessage({ id: 'pagination.btn.prev' })}
        disabled={page === 1}
        onClick={() => handlePageChange(page - 1)}
      />
      <ul className={styles.pages}>
        {getPaginationItems(page, totalPages).map(
          (item): ReactElement =>
            typeof item === 'number' ? (
              <li key={item}>
                <Button
                  size="small"
                  mode={item === page ? 'secondary' : 'tertiary'}
                  color="dark"
                  className={styles.page}
                  aria-label={intl.formatMessage(
                    { id: 'pagination.page' },
                    { page: item },
                  )}
                  aria-current={item === page ? 'page' : undefined}
                  onClick={() => handlePageChange(item)}
                >
                  {item}
                </Button>
              </li>
            ) : (
              <li key={item} className={styles.ellipsis} aria-hidden="true">
                …
              </li>
            ),
        )}
      </ul>
      <span className={styles.summary}>
        {intl.formatMessage(
          { id: 'pagination.summary' },
          { current: page, total: totalPages },
        )}
      </span>
      <Button
        size="small"
        mode="tertiary"
        color="dark"
        icon={<ArrowRight color="currentColor" />}
        aria-label={intl.formatMessage({ id: 'pagination.btn.next' })}
        disabled={page === totalPages}
        onClick={() => handlePageChange(page + 1)}
      />
    </nav>
  );
};

export { Pagination };
